import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Check, AlertTriangle, Landmark } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { fmtNum, round2, uuid } from "@/lib/format";
import { NumPad, AmountDisplay } from "@/components/NumPad";
import { BigButton } from "@/components/ui-kit";
import CustomerPicker from "@/components/customers/CustomerPicker";

export const HOUSE_FOR = ["owner", "owner_family", "staff", "staff_family"];

export const saleFrom = ({ cart, totals, promo, mode, paid, given, customer, rx, house, lang }) => {
  const id = uuid();
  const charity = round2(cart.filter((l) => l.charity).reduce((s, l) => s + l.price * l.qty, 0));
  const total = round2(totals.total - charity);
  return {
    id, receipt_no: id.slice(0, 6).toUpperCase(), lang: lang || localStorage.getItem("sp_lang") || "ar", ...(rx ? { rx } : {}), ...(house ? { house } : {}),
    items: cart.filter((l) => !l.charity).map((l) => ({ ...l })), subtotal: round2(totals.subtotal - charity), discount: totals.discount, total,
    charity, charity_name: charity ? (cart.find((l) => l.charity)?.name || "") : "",
    promotion_id: promo?.id || null, payment: mode, paid: Math.min(paid, total), given, customer_id: customer?.id || null, customer_name: customer?.name || null,
  };
};

export default function CheckoutDialog({ mode, onClose, cart, totals, promo, onDone, initialCustomer = null, rxPrefill = null, gate = (fn) => fn(), extra = null }) {
  const { t, money, dispatch, staff, view, lang } = useApp();
  const [amount, setAmount] = useState("");
  const [customerId, setCustomerId] = useState(null);
  useEffect(() => { setAmount(""); setCustomerId(initialCustomer); }, [mode, initialCustomer]);

  const customer = customerId ? view.customers[customerId] : null;
  const isCash = mode === "cash";
  const isHouse = mode === "house";
  const [useAdv, setUseAdv] = useState(false);
  useEffect(() => setUseAdv(false), [mode, customerId]);
  const advance = customer ? Math.max(0, -(customer.balance || 0)) : 0;
  const isAdv = useAdv && advance > 0;
  const [house, setHouse] = useState({ for: "owner", note: "" });
  useEffect(() => { if (mode === "house") setHouse({ for: "owner", note: "" }); }, [mode]);
  const num = Number(amount || 0);
  const given = isCash && !isAdv ? (amount ? num : totals.total) : null;
  const paid = isHouse || isAdv ? 0 : isCash ? totals.total : num;
  const credit = (isCash && !isAdv) || isHouse ? 0 : round2(totals.total - paid);
  const overLimit = customer && customer.credit_limit > 0 && customer.balance + credit > customer.credit_limit;
  const valid = isHouse ? house.note.trim().length > 0 : isAdv ? true : isCash ? given >= totals.total : !!customer && paid <= totals.total;
  const presets = [50, 100, 500, 1000, 2000].map((p) => Math.ceil(totals.total / p) * p).filter((v, i, a) => v > totals.total && a.indexOf(v) === i).slice(0, 3);
  const rxItems = cart.filter((l) => view.products[l.product_id]?.rx_required || rxPrefill);
  const [rx, setRx] = useState({ doctor: "", patient: "", number: "" });
  useEffect(() => { if (mode) setRx({ doctor: rxPrefill?.doctor_name || "", patient: rxPrefill?.patient_name || "", number: rxPrefill?.code || "" }); }, [mode, rxPrefill]);
  const rxOk = !rxItems.length || (rx.doctor.trim() && rx.patient.trim());

  const confirm = () => {
    if (!rxOk) return toast.error(t("rx_gate_required"));
    if (isHouse) return gate(doConfirm);
    doConfirm();
  };
  const doConfirm = () => {
    const sale = { ...saleFrom({ cart, totals, promo, lang, mode: isAdv ? "advance" : mode, paid, given, customer: isHouse ? null : customer, house: isHouse ? { for: house.for, note: house.note.trim() } : null, rx: rxItems.length ? { ...rx, items: rxItems.map((l) => l.name), clinic: rxPrefill?.clinic_name || "" } : null }), ...(extra || {}) };
    if (rxPrefill?.code) api.post(`/rx/${rxPrefill.code}/dispense`, { sale_id: sale.id }).catch(() => {});
    if (!dispatch("sale.create", sale)) return;
    toast.success(t("sale_saved"));
    onDone({ ...sale, staff_name: staff.name, created_at: new Date().toISOString(), balance_after: customer ? round2(customer.balance + credit) : null });
  };

  return (
    <Dialog open={!!mode} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md lg:max-w-4xl max-h-[94vh] overflow-y-auto" data-testid="checkout-dialog">
        <DialogTitle className="font-heading text-2xl">{isHouse ? t("pay_house") : isCash ? t("pay_cash") : t("pay_credit")}</DialogTitle>
        <div className={`grid gap-4 ${isHouse ? "" : "lg:grid-cols-2"}`}>
        <div className="space-y-3">
        <div className="flex justify-between items-end rounded-xl bg-secondary p-3">
          <span className="font-bold">{t("total")}</span>
          <span className="text-3xl font-extrabold" data-testid="checkout-total">{money(totals.total)}</span>
        </div>
        {isHouse && (
          <div className="space-y-3" data-testid="house-form">
            <div className="rounded-xl bg-[#FFF7D1] border border-[#E2B93B] p-3 text-sm text-[#6B4F00]" data-testid="house-hint">{t("house_hint")}{!["owner", "manager"].includes(staff?.role) && <div className="mt-1 font-bold" data-testid="house-gate-note">{t("house_gate_note")}</div>}</div>
            <div>
              <div className="text-sm font-bold mb-1.5">{t("house_for")}</div>
              <div className="grid grid-cols-2 gap-2">
                {HOUSE_FOR.map((k) => <button key={k} type="button" onClick={() => setHouse({ ...house, for: k })} className={`h-11 rounded-xl border font-bold text-sm ${house.for === k ? "bg-[#1F4E8C] text-white border-[#1F4E8C]" : "bg-white border-border"}`} data-testid={`house-for-${k}`}>{t(`house_${k}`)}</button>)}
              </div>
            </div>
            <div>
              <div className="text-sm font-bold mb-1.5">{t("house_note")} *</div>
              <textarea value={house.note} onChange={(e) => setHouse({ ...house, note: e.target.value })} rows={2} placeholder={t("house_note_ph")} className="w-full rounded border border-[#ABADB3] px-3 py-2 text-sm" data-testid="house-note" />
            </div>
          </div>
        )}
        {!isHouse && <div>
          <div className="text-sm font-bold mb-1.5">{isCash ? t("customer_optional") : t("choose_family_tab")}</div>
          <CustomerPicker value={customerId} onChange={setCustomerId} />
        </div>}
        {customer && advance > 0 && !isHouse && (
          <button type="button" onClick={() => setUseAdv((v) => !v)} className={`w-full h-12 rounded-xl border-2 font-bold text-sm flex items-center justify-center gap-2 ${isAdv ? "bg-[#1F4E8C] text-white border-[#1F4E8C]" : "border-[#1F4E8C] text-[#1F4E8C] bg-white"}`} data-testid="checkout-use-advance-btn">
            <Landmark className="w-5 h-5" />{t("pay_with_advance")} · {money(advance)}
          </button>
        )}
        {isAdv && (
          <div className="rounded-xl p-3 text-sm font-bold bg-[#1F4E8C]/10 text-[#1F4E8C]" data-testid="checkout-advance-info">
            {t("advance_applied")}: {money(Math.min(advance, totals.total))}{totals.total > advance && <div className="text-[#A85A12]">{t("advance_rest_debt")}: {money(round2(totals.total - advance))}</div>}
            <div>{t("balance_now")}: {money(customer.balance)} → {money(round2(customer.balance + totals.total))}</div>
          </div>
        )}
        {customer && !isCash && !isAdv && (
          <div className={`rounded-xl p-3 text-sm font-bold ${overLimit ? "bg-destructive/10 text-destructive" : "bg-[#E67E22]/10 text-[#A85A12]"}`} data-testid="checkout-credit-info">
            {overLimit && <AlertTriangle className="inline w-4 h-4 me-1" />}
            {t("balance_now")}: {money(customer.balance)} → {money(customer.balance + credit)}
            {overLimit && <div>{t("over_limit")} ({money(customer.credit_limit)})</div>}
          </div>
        )}
        {!isHouse && !isAdv && <AmountDisplay value={amount} label={isCash ? t("cash_given") : t("paid_now")} testid="checkout-amount-display" />}
        {rxItems.length > 0 && (
          <div className="rounded border border-[#E74C3C]/50 bg-[#FDF2F2] p-3 space-y-2" data-testid="rx-gate">
            <div className="text-sm font-bold text-[#C0392B] flex items-center gap-1"><AlertTriangle className="w-4 h-4" />{t("rx_gate_title")}: {rxItems.map((l) => l.name).join(", ")}</div>
            <div className="grid grid-cols-3 gap-2">
              <input placeholder={t("doctor")} value={rx.doctor} onChange={(e) => setRx({ ...rx, doctor: e.target.value })} className="h-10 rounded border border-[#ABADB3] px-2 text-sm" data-testid="rx-gate-doctor" />
              <input placeholder={t("patient")} value={rx.patient} onChange={(e) => setRx({ ...rx, patient: e.target.value })} className="h-10 rounded border border-[#ABADB3] px-2 text-sm" data-testid="rx-gate-patient" />
              <input dir="ltr" placeholder={t("rx_number")} value={rx.number} onChange={(e) => setRx({ ...rx, number: e.target.value })} className="h-10 rounded border border-[#ABADB3] px-2 text-sm" data-testid="rx-gate-number" />
            </div>
          </div>
        )}
        </div>
        <div className="space-y-3">
        {isCash && !isAdv && (
          <div className="flex gap-2">
            <button type="button" onClick={() => setAmount(String(totals.total))} className="flex-1 h-12 rounded-xl border-2 border-[#1F9D55] text-[#1F9D55] font-bold" data-testid="checkout-exact-btn">{t("exact")}</button>
            {presets.map((p) => <button key={p} type="button" onClick={() => setAmount(String(p))} className="flex-1 h-12 rounded-xl border border-border bg-white font-bold" data-testid={`checkout-preset-${p}`}>{fmtNum(p)}</button>)}
          </div>
        )}
        {!isHouse && !isAdv && <NumPad value={amount} onChange={setAmount} testid="pos-numpad" />}
        {isCash && !isAdv && given > totals.total && <div className="text-center text-xl font-extrabold text-[#1F9D55]" data-testid="checkout-change">{t("change")}: {money(given - totals.total)}</div>}
        {!isCash && !isHouse && !isAdv && <div className="text-center font-bold" data-testid="checkout-credit-amount">{t("on_tab")}: {money(credit)}</div>}
        <BigButton variant={isCash ? "green" : isHouse ? "soft" : "amber"} disabled={!valid || !rxOk} onClick={confirm} className="w-full" data-testid="checkout-confirm-btn"><Check className="w-6 h-6" />{t("confirm_sale")}</BigButton>
        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
